import React, { useState } from "react";
import { MapPin, Clock, CheckCircle2, Users, Sparkles, Globe, Send } from "lucide-react";
import Reveal from "../components/Reveal";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { EVENTS, getUpcomingEvents, EVENT_TYPES, BUDGET_RANGES, saveToStorage } from "../mock/mock";

const Booking = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "", org: "", email: "", phone: "", date: "",
    type: "", location: "", budget: "", details: "",
  });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const upcoming = getUpcomingEvents();

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.date || !form.type || !form.location) {
      toast({ title: "Missing required fields", description: "Please complete all required (*) fields.", variant: "destructive" });
      return;
    }
    saveToStorage("atom_bookings", form);
    toast({ title: "Inquiry submitted! \uD83C\uDF89", description: "We'll be in touch within 48 hours with availability and a custom quote." });
    setForm({ name: "", org: "", email: "", phone: "", date: "", type: "", location: "", budget: "", details: "" });
  };

  const expectations = [
    "Response within 48 hours", "Availability confirmation", "Custom quote for your event",
    "Detailed contract and rider", "Clear communication throughout",
  ];

  const highlights = [
    { icon: Users, t: "Full Band Only", d: "A.T.O.M. performs as a complete ensemble\u2014no duo or trio options." },
    { icon: Sparkles, t: "Professional Production", d: "We arrive early for soundcheck and coordinate with your production team." },
    { icon: Clock, t: "Investment", d: "Pricing reflects our concert-ready experience. Quotes provided upon inquiry." },
    { icon: Globe, t: "National Reach", d: "We perform throughout the United States for the right events." },
  ];

  return (
    <div className="overflow-hidden">
      <section className="pt-40 pb-16 px-5 text-center relative">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[500px] h-[400px] bg-[#e0b04c]/8 rounded-full blur-[130px] pointer-events-none" />
        <Reveal className="relative z-10 max-w-3xl mx-auto">
          <p className="text-[#e0b04c] text-sm tracking-[0.3em] uppercase font-medium mb-5">Hire A.T.O.M.</p>
          <h1 className="font-serif-display text-5xl md:text-6xl text-[#f5f0e8] leading-tight">
            Request <span className="text-gold-gradient">Availability</span>
          </h1>
          <p className="text-[#b3a998] text-lg leading-relaxed mt-6 max-w-2xl mx-auto">
            Ready to bring the magic of Frankie Beverly &amp; Maze to your event?
            Complete the form below and we'll be in touch within 48 hours.
          </p>
        </Reveal>
      </section>

      {/* UPCOMING EVENTS STRIP */}
      <section className="px-5 pb-16">
        <Reveal className="max-w-6xl mx-auto">
          <p className="text-[#e0b04c] text-sm tracking-[0.3em] uppercase font-medium mb-6">See Us Live &mdash; Upcoming Events</p>
        </Reveal>
        <div className="max-w-6xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {upcoming.map((e, i) => (
            <Reveal key={i} delay={(i % 3) * 80}>
              <div className="p-5 rounded-xl bg-[#12100c] border border-white/5 hover:border-[#e0b04c]/30 transition-all duration-400">
                <span className="text-[#e0b04c] text-sm font-medium">{e.date}</span>
                <h3 className="font-serif-display text-xl text-[#f5f0e8] mt-1 mb-2">{e.venue}</h3>
                <div className="flex items-start gap-2 text-[#9c917f] text-sm">
                  <MapPin size={15} className="text-[#e0b04c] mt-0.5 flex-shrink-0" />
                  <span>{e.address}{e.city && `, ${e.city}`}</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FORM + SIDEBAR */}
      <section className="px-5 pb-24">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-8">
          {/* Form */}
          <Reveal className="lg:col-span-2">
            <form onSubmit={submit} className="bg-[#12100c] border border-white/8 rounded-2xl p-8 space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#c9bda9] mb-1.5 block text-sm">Your Name *</Label>
                  <Input value={form.name} onChange={(e) => update("name", e.target.value)} className="bg-[#1a1710] border-white/10 text-[#f5f0e8] focus-visible:ring-[#e0b04c]" />
                </div>
                <div>
                  <Label className="text-[#c9bda9] mb-1.5 block text-sm">Organization</Label>
                  <Input value={form.org} onChange={(e) => update("org", e.target.value)} className="bg-[#1a1710] border-white/10 text-[#f5f0e8] focus-visible:ring-[#e0b04c]" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#c9bda9] mb-1.5 block text-sm">Email Address *</Label>
                  <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="bg-[#1a1710] border-white/10 text-[#f5f0e8] focus-visible:ring-[#e0b04c]" />
                </div>
                <div>
                  <Label className="text-[#c9bda9] mb-1.5 block text-sm">Phone Number</Label>
                  <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} className="bg-[#1a1710] border-white/10 text-[#f5f0e8] focus-visible:ring-[#e0b04c]" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#c9bda9] mb-1.5 block text-sm">Event Date *</Label>
                  <Input type="date" value={form.date} onChange={(e) => update("date", e.target.value)} className="bg-[#1a1710] border-white/10 text-[#f5f0e8] focus-visible:ring-[#e0b04c] [color-scheme:dark]" />
                </div>
                <div>
                  <Label className="text-[#c9bda9] mb-1.5 block text-sm">Event Type *</Label>
                  <Select value={form.type} onValueChange={(v) => update("type", v)}>
                    <SelectTrigger className="bg-[#1a1710] border-white/10 text-[#f5f0e8]"><SelectValue placeholder="Select event type" /></SelectTrigger>
                    <SelectContent className="bg-[#1a1710] border-white/10 text-[#f5f0e8]">
                      {EVENT_TYPES.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#c9bda9] mb-1.5 block text-sm">Event Location *</Label>
                  <Input value={form.location} onChange={(e) => update("location", e.target.value)} placeholder="City, State" className="bg-[#1a1710] border-white/10 text-[#f5f0e8] focus-visible:ring-[#e0b04c]" />
                </div>
                <div>
                  <Label className="text-[#c9bda9] mb-1.5 block text-sm">Budget Range</Label>
                  <Select value={form.budget} onValueChange={(v) => update("budget", v)}>
                    <SelectTrigger className="bg-[#1a1710] border-white/10 text-[#f5f0e8]"><SelectValue placeholder="Select budget range" /></SelectTrigger>
                    <SelectContent className="bg-[#1a1710] border-white/10 text-[#f5f0e8]">
                      {BUDGET_RANGES.map((b) => (<SelectItem key={b} value={b}>{b}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-[#c9bda9] mb-1.5 block text-sm">Tell Us About Your Event</Label>
                <Textarea value={form.details} onChange={(e) => update("details", e.target.value)} rows={5} placeholder="Share details about your event, audience, and vision..." className="bg-[#1a1710] border-white/10 text-[#f5f0e8] focus-visible:ring-[#e0b04c] resize-none" />
              </div>
              <button type="submit" className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-b from-[#e8bc55] to-[#c99339] text-[#0c0a08] font-semibold px-8 py-4 rounded-md hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(224,176,76,0.35)] transition-all duration-300">
                <Send size={18} /> Submit Inquiry
              </button>
            </form>
          </Reveal>

          {/* Sidebar */}
          <Reveal delay={150} className="space-y-5">
            <div className="p-7 rounded-2xl bg-[#12100c] border border-white/8">
              <h3 className="font-serif-display text-2xl text-[#f5f0e8] mb-5">What to Expect</h3>
              <ul className="space-y-3">
                {expectations.map((t) => (
                  <li key={t} className="flex items-center gap-3 text-[#c9bda9] text-sm">
                    <CheckCircle2 size={18} className="text-[#e0b04c] flex-shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </div>
            {highlights.map((h, i) => (
              <div key={i} className="p-6 rounded-2xl bg-[#12100c] border border-white/8 hover:border-[#e0b04c]/30 transition-all duration-400">
                <div className="flex items-center gap-3 mb-2">
                  <h.icon className="text-[#e0b04c]" size={20} />
                  <h4 className="font-serif-display text-lg text-[#f5f0e8]">{h.t}</h4>
                </div>
                <p className="text-[#9c917f] text-sm leading-relaxed">{h.d}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Booking;
